import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { footerSettingsAPI, siteDocumentAPI } from '../services/api';
import { Mail, Phone } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';
import { useLanguage } from '../context/LanguageContext';

const DEFAULT_DESCRIPTION = "The Accra Centre for Applied Research publishes independent "
  + "research and open data on Ghana's economy, education, and trade.";

// lucide-react doesn't ship brand/logo icons (removed to avoid
// trademark/bundle-size concerns) -- simple inline SVGs instead,
// matching the pattern already used elsewhere for custom icons.
const LinkedinIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 110-4.124 2.062 2.062 0 010 4.124zM7.114 20.452H3.558V9h3.556v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const YoutubeIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 1.877-.287 1.79h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z"/>
  </svg>
);
const InstagramIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const SOCIAL_LINKS = [
  { key: 'linkedin_url', Icon: LinkedinIcon, label: 'LinkedIn' },
  { key: 'youtube_url', Icon: YoutubeIcon, label: 'YouTube' },
  { key: 'facebook_url', Icon: FacebookIcon, label: 'Facebook' },
  { key: 'instagram_url', Icon: InstagramIcon, label: 'Instagram' },
  { key: 'twitter_url', Icon: XIcon, label: 'X (Twitter)' },
];

const Footer = () => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState({ description: DEFAULT_DESCRIPTION });
  const [privacyPolicy, setPrivacyPolicy] = useState(null);

  useEffect(() => {
    footerSettingsAPI.get()
      .then(res => setSettings(res.data))
      .catch(() => { /* fall back to the default already in state */ });
    // Only shows the link once a PDF has actually been uploaded in
    // the admin panel -- a 404 here just means none exists yet.
    siteDocumentAPI.get('privacy_policy')
      .then(res => setPrivacyPolicy(res.status === 200 ? res.data : null))
      .catch(() => setPrivacyPolicy(null));
  }, []);

  const activeSocialLinks = SOCIAL_LINKS.filter(s => settings[s.key]);

  return (
    <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-white font-bold text-lg mb-4">ACAR</h3>
            <p className="text-sm mb-4">{settings.description}</p>

            <div className="mb-4">
              <p className="text-xs text-gray-400 mb-2">{t('footer.newsletterPrompt')}</p>
              <NewsletterSignup variant="dark" source="footer" />
            </div>

            {(settings.contact_email || settings.contact_phone) && (
              <div className="space-y-1.5 text-sm">
                {settings.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 hover:text-white">
                    <Mail size={14}/> {settings.contact_email}
                  </a>
                )}
                {settings.contact_phone && (
                  <a href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 hover:text-white">
                    <Phone size={14}/> {settings.contact_phone}
                  </a>
                )}
              </div>
            )}

            {activeSocialLinks.length > 0 && (
              <div className="flex items-center gap-3 mt-4">
                {activeSocialLinks.map(({ key, Icon, label }) => (
                  <a key={key} href={settings[key]} target="_blank" rel="noopener noreferrer"
                    aria-label={label} className="text-gray-400 hover:text-white transition">
                    <Icon width={18} height={18}/>
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t('nav.publications')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/research" className="hover:text-white">{t('nav.research')}</Link></li>
              <li><Link to="/publications/articles-journals" className="hover:text-white">{t('nav.ghanaianArticles')}</Link></li>
              <li><Link to="/publications/current-affairs" className="hover:text-white">{t('nav.currentAffairs')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t('nav.explore')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/datasets" className="hover:text-white">{t('nav.exploreDatasets')}</Link></li>
              <li><Link to="/open-book" className="hover:text-white">{t('nav.openBook')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4">{t('nav.about')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">{t('footer.aboutAcar')}</Link></li>
              <li><Link to="/download-data?donate=true" className="hover:text-white">{t('nav.donate')}</Link></li>
              {privacyPolicy?.file_url && (
                <li>
                  <a href={privacyPolicy.file_url} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                    {privacyPolicy.title}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
          <p>&copy; {new Date().getFullYear()} Accra Centre for Applied Research. {t('footer.allRightsReserved')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
